// Setup wizard (README "Scope 1"): brand → provider + keys → test send to the admin's own number → webhook URL → done.
import { callFn, functionsUrl, rpc, sb } from "../sb.ts";
import { applyBrand, loadBrand, logoUrl, type Brand } from "../brand.ts";
import { h, mount, toast } from "../ui.ts";
import { t } from "../i18n.ts";

const STEPS = () => [t("Brand"), t("Provider"), t("Test send"), t("Webhook")];

/** skip: shown during the first set-up only — try the app (e.g. the AI) before WhatsApp is connected */
export async function setupWizard(root: HTMLElement, _brand: Brand, done: () => void, skip?: () => void) {
  const { data: s } = await sb.from("settings").select("*").maybeSingle();
  const { data: lines } = await sb.from("wa_lines").select("line, label").order("created_at");
  let step = 0;
  const show = (n: number) => { step = n; render(); };
  const frame = (body: Node) => mount(root, h("main", { class: "card wide" },
    h("ol", { class: "steps" }, STEPS().map((t, i) => h("li", { class: i === step ? "on" : i < step ? "done" : "" }, t))), body));

  function render() {
    if (step === 0) {
      const company = h("input", { value: s?.company_name ?? "", required: true });
      const app = h("input", { value: s?.app_name ?? "", placeholder: t("same as the company name") });
      const sender = h("input", { value: s?.sender_name ?? "", placeholder: t("same as the company name") });
      const primary = h("input", { type: "color", value: s?.color_primary ?? "#1f6feb" });
      const accent = h("input", { type: "color", value: s?.color_accent ?? "#25d366" });
      const cc = h("input", { value: s?.default_cc ?? "968", inputmode: "numeric", size: 4 });
      const file = h("input", { type: "file", accept: "image/png,image/jpeg,image/svg+xml,image/webp" });
      let logoPath: string | null = s?.logo_path ?? null;
      const preview = h("img", { class: "logo", src: logoUrl(logoPath) ?? "", alt: "", hidden: !logoPath });
      file.addEventListener("change", async () => {
        const f = file.files?.[0];
        if (!f) return;
        if (f.size > 1024 * 1024) return toast(t("Logo: 1 MB at most"), true);
        const path = `logo-${crypto.randomUUID()}.${(f.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "")}`;
        const { error } = await sb.storage.from("brand").upload(path, f, { contentType: f.type, upsert: false });
        if (error) return toast(error.message, true);
        logoPath = path; preview.src = logoUrl(path)!; preview.hidden = false;
      });
      frame(h("form", { onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          await rpc("save_brand", { p_company: company.value, p_app: app.value, p_logo: logoPath, p_primary: primary.value,
            p_accent: accent.value, p_sender: sender.value, p_cc: cc.value });
          applyBrand(await loadBrand());
          Object.assign(s ?? {}, { company_name: company.value, app_name: app.value, sender_name: sender.value, color_primary: primary.value,
            color_accent: accent.value, default_cc: cc.value, logo_path: logoPath });
          show(1);
        } catch (err) { toast((err as Error).message, true); }
      } },
        h("h2", {}, t("Your brand")),
        h("label", {}, t("Company name"), company),
        h("label", {}, t("App name (on the phone's home screen)"), app),
        h("label", {}, t("Sender display name"), sender),
        h("div", { class: "row" }, h("label", {}, t("Main colour"), primary), h("label", {}, t("Accent colour"), accent),
          h("label", {}, t("Country code for local numbers"), cc)),
        h("label", {}, t("Logo / app icon (PNG, JPG, SVG; square works best)"), file), preview,
        h("div", { class: "actions" }, h("button", { class: "primary", type: "submit" }, t("Next")))));
    } else if (step === 1) {
      const provider = h("select", {}, h("option", { value: "twilio", selected: s?.provider !== "360dialog" }, "Twilio"),
        h("option", { value: "360dialog", selected: s?.provider === "360dialog" }, "360dialog"), h("option", { value: "meta", disabled: true }, t("Meta Cloud API (coming)")));
      const account = h("input", { value: s?.provider === "twilio" ? s?.provider_account ?? "" : "", placeholder: "AC…", autocomplete: "off", spellcheck: false });
      const token = h("input", { type: "password", autocomplete: "new-password", placeholder: t("paste to set or replace") });
      const line = h("input", { value: lines?.[0]?.line ? "+" + lines[0].line : "", placeholder: t("+968 9xxx xxxx (your WhatsApp sender)") });
      const out = h("p", { class: "muted" });
      const accountRow = h("label", {}, t("Twilio Account SID"), account);
      const tokenLabel = h("span", {});
      const help = h("p", { class: "muted" });
      const is360 = () => provider.value === "360dialog";
      const toggle = () => {
        accountRow.hidden = is360();
        tokenLabel.textContent = is360() ? t("360dialog API key") : t("Twilio Auth Token");
        help.textContent = is360() ? t("360dialog Hub → your number → API key (generate one if needed). The webhook is set automatically.") : "";
      };
      provider.addEventListener("change", toggle);
      toggle();
      rpc<boolean>("provider_key_set", { p_name: "provider_auth_token" }).then((set) => { if (set) token.placeholder = t("saved — paste only to replace"); });
      frame(h("form", { onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          await rpc("save_provider", { p_provider: provider.value, p_account: is360() ? null : account.value.trim(), p_line: line.value, p_label: "" });
          if (token.value.trim()) await rpc("save_provider_secret", { p_name: "provider_auth_token", p_value: token.value.trim() });
          token.value = "";
          out.textContent = is360() ? t("Checking the key with 360dialog…") : t("Checking the keys with Twilio…");
          const r = await callFn("wa-send", { mode: "check" });
          if (!r.ok) { out.textContent = "✗ " + (r.error ?? t("check failed")); return; }
          if (r.webhook && !r.webhook.ok) { out.textContent = t("✓ Key works, but the webhook could not be set: {error}", { error: r.webhook.error ?? "" }); return; }
          out.textContent = t("✓ Connected to {name}", { name: r.account }) + (r.webhook?.ok ? " · " + t("webhook set") : "");
          if (s) { s.provider = provider.value; s.provider_account = is360() ? null : account.value.trim(); }
          setTimeout(() => show(2), 600);
        } catch (err) { out.textContent = "✗ " + (err as Error).message; }
      } },
        h("h2", {}, t("WhatsApp provider")),
        h("p", { class: "muted" }, t("Your company's own provider account: messages are billed to it directly. Keys are stored in this project's Vault and never shown again.")),
        h("label", {}, t("Provider"), provider),
        accountRow,
        h("label", {}, tokenLabel, token),
        help,
        h("label", {}, t("WhatsApp sender number"), line),
        out,
        h("div", { class: "actions" }, h("button", { type: "button", onclick: () => show(0) }, t("Back")),
          skip && h("button", { type: "button", onclick: skip }, t("Skip for now")),
          h("button", { class: "primary", type: "submit" }, t("Save and check"))),
        skip && h("p", { class: "muted" }, t("Skip: open the app without WhatsApp (to try the AI first). Settings opens this wizard again."))));
    } else if (step === 2) {
      const to = h("input", { value: s?.test_phone ? "+" + s.test_phone : "", placeholder: "+968 9xxx xxxx", required: true });
      const is360 = s?.provider === "360dialog";
      const tpl = h("input", { placeholder: is360 ? t("optional: template name (needed outside the 24-hour window)") : t("optional: Content SID HX… (needed outside the 24-hour window)"), spellcheck: false });
      const tplLang = h("input", { value: "en", size: 6, spellcheck: false, dir: "ltr" });
      const out = h("p", { class: "muted" });
      frame(h("form", { onsubmit: async (e: Event) => {
        e.preventDefault();
        out.textContent = t("Sending…");
        const r = await callFn("wa-send", { mode: "test", to: to.value, template: tpl.value.trim() || undefined, lang: tplLang.value.trim() || undefined });
        if (r.sent) { out.textContent = t("✓ Sent to +{to}. Check your phone, then continue.", { to: r.to }); s && (s.test_phone = r.to); }
        else out.textContent = "✗ " + (r.error ?? t("not sent")) + (r.hint ? " — " + r.hint : "");
      } },
        h("h2", {}, t("Test send")),
        h("p", { class: "muted" }, is360 ? "" : t("One message to your own number. With the Twilio sandbox, send the join code from your phone first. "),
          t("A business number can start a conversation only with an approved template: paste its Content SID, or message the number from your phone first and send within 24 hours.")),
        h("label", {}, t("Your own WhatsApp number"), to),
        h("div", { class: "row" }, h("label", {}, t("Template"), tpl), is360 && h("label", {}, t("Language code"), tplLang)),
        out,
        h("div", { class: "actions" }, h("button", { type: "button", onclick: () => show(1) }, t("Back")),
          h("button", { type: "submit" }, t("Send test")), h("button", { class: "primary", type: "button", onclick: () => show(3) }, t("Next")))));
    } else {
      const hook = functionsUrl("wa-webhook");
      const finish = h("div", { class: "actions" }, h("button", { onclick: () => show(2) }, t("Back")),
        h("button", { class: "primary", onclick: async () => {
          try { await rpc("finish_setup", { p_test_phone: s?.test_phone ?? null }); done(); } catch (e) { toast((e as Error).message, true); }
        } }, t("Done — open the inbox")));
      if (s?.provider === "360dialog") {
        frame(h("div", {}, h("h2", {}, t("Webhook")),
          h("p", {}, t("Already set: the check in step 2 pointed your 360dialog number at this app (with a secret only this app knows).")),
          h("p", { class: "muted" }, t("Send a message from your phone to the business number: it shows up in the inbox.")), finish));
        return;
      }
      frame(h("div", {},
        h("h2", {}, t("Webhook")),
        h("p", {}, t("In the Twilio console, open your WhatsApp sender (or the sandbox settings) and paste this URL in both "),
          h("b", {}, t("“When a message comes in”")), t(" and "), h("b", {}, t("“Status callback URL”")), t(" (method POST):")),
        h("div", { class: "copy" }, h("code", {}, hook), h("button", { onclick: async () => { await navigator.clipboard.writeText(hook); toast("Copied"); } }, t("Copy"))),
        h("p", { class: "muted" }, t("Every call is checked against your Auth Token signature. Then reply to your test message from your phone: it shows up in the inbox.")),
        finish));
    }
  }

  render();
}
